import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import cornerUrl from "../../assets/brand/login-corner.svg";
import klNavyUrl from "../../assets/brand/login-kl-navy.svg";
import mascotUrl from "../../assets/brand/login-mascot.webp";
import logoUrl from "../../assets/brand/sea-games-2027-logo-white.svg";
import { LanguageSwitcher } from "../i18n/language-switcher";

/**
 * The signed-out shell — sign-in, change password, 2FA, no access — from the
 * designer's prototype (SEAGAMES-UI admin.css "v6 — Sign in", `.lgn*`).
 *
 * Desktop (≥ 64rem): a navy brand half — logo, slogan over a red→yellow rule,
 * the welcome line, the mascot on the red/yellow sweep, the navy KL skyline
 * behind — and a light half holding the card, with halftone dots and the
 * ribbon corner top-right.
 *
 * Phones: one navy page, the brand block on top and the card under it, the
 * sweep reduced to two stripes along the bottom.
 *
 * The language switcher (i18n is mandatory, the prototype has none) sits in the
 * logo row on phones and in the light half's bottom-right corner on desktop,
 * clear of the corner art. Two instances, one `display:none` at a time, so
 * each can take the colours of its own ground.
 *
 * Sizes are the prototype's px ÷ 16 in rem (CLAUDE.md § Conventions); her
 * 720px step is `min-[45rem]`, her 1024px step is `lg`. Decorative images are
 * `aria-hidden` with empty alt.
 */
export function AuthSplit({ children }: { children: ReactNode }) {
	const { t } = useTranslation("auth");
	return (
		<div className="relative isolate flex min-h-dvh flex-col overflow-hidden bg-gms-navy lg:flex-row lg:bg-gms-soft">
			<section
				className="relative bg-gms-navy bg-[position:center_40%] bg-cover bg-no-repeat px-[1rem] pt-[calc(1.75rem+env(safe-area-inset-top))] text-white min-[45rem]:px-[2.5rem] min-[45rem]:pt-[3rem] min-[45rem]:pb-[1.25rem] lg:flex lg:flex-[0_0_50%] lg:flex-col lg:overflow-hidden lg:bg-[position:center_70%] lg:px-[4rem] lg:py-[4.5rem]"
				style={{ backgroundImage: `url(${klNavyUrl})` }}
			>
				<div
					aria-hidden
					className="absolute inset-0 z-0 bg-[linear-gradient(180deg,rgba(27,29,92,0.55),rgba(46,49,146,0.82)_70%,#2e3192)] lg:bg-[linear-gradient(180deg,rgba(27,29,92,0.4),rgba(46,49,146,0.7))]"
				/>
				<div className="relative z-[1] grid max-w-[28.75rem] gap-[0.875rem]">
					<div className="flex items-start justify-between gap-[1rem]">
						<img
							src={logoUrl}
							alt={t("split.logoAlt")}
							width={566}
							height={313}
							decoding="async"
							className="h-auto w-[11.25rem] min-[45rem]:w-[13.75rem] lg:w-[16.25rem]"
						/>
						<div className="-mr-[0.5rem] lg:hidden">
							<LanguageSwitcher variant="onblue" dropdownClass="dropdown-end" />
						</div>
					</div>
					<h1 className="mt-[0.375rem] font-display font-bold text-[2.125rem] uppercase leading-[0.95] tracking-[0.005em] min-[45rem]:text-[2.75rem] lg:text-[3.25rem]">
						{t("split.taglineLine1")}
						<br />
						{t("split.taglineLine2")}
					</h1>
					<span
						aria-hidden
						className="block h-[0.25rem] w-full max-w-[18.75rem] rounded-[0.125rem] bg-linear-to-r from-gms-nav-red to-gms-nav-yellow"
					/>
					<p className="max-w-[36ch] text-[0.875rem] text-white/[0.86] leading-[1.5] lg:text-[1rem]">
						{t("split.welcome")}
					</p>
				</div>
				<img
					src={mascotUrl}
					alt=""
					aria-hidden
					decoding="async"
					width={545}
					height={820}
					className="pointer-events-none absolute right-[12%] bottom-[-1.25rem] z-[2] hidden h-[min(58vh,32.5rem)] w-auto select-none lg:block"
				/>
				<svg
					viewBox="0 0 400 300"
					preserveAspectRatio="none"
					aria-hidden
					focusable="false"
					className="absolute right-0 bottom-0 z-[1] hidden h-[44%] w-[72%] lg:block"
				>
					<path d="M400 40C330 150 210 240 40 300H120C260 250 350 170 400 90Z" fill="#ed1c24" />
					<path d="M400 110C350 190 270 255 170 300H260C320 266 368 220 400 170Z" fill="#ffcb05" />
				</svg>
			</section>

			<section className="relative isolate flex flex-1 flex-col px-[1rem] pt-[1.5rem] pb-[calc(5rem+env(safe-area-inset-bottom))] min-[45rem]:px-[1.5rem] lg:items-center lg:justify-center lg:bg-gms-soft lg:px-[2.5rem] lg:py-[3rem]">
				{/* Phones: the sweep as two stripes along the bottom of the navy page. */}
				<div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[5.625rem] lg:hidden" style={STRIPES} />
				<img
					src={cornerUrl}
					alt=""
					aria-hidden
					decoding="async"
					width={692}
					height={938}
					className="pointer-events-none absolute top-[-6.875rem] right-[-2.5rem] z-0 hidden h-auto w-[22.5rem] select-none [clip-path:inset(0_0_44%_0)] lg:block"
				/>
				<span aria-hidden className="absolute top-0 right-0 -z-10 hidden h-[16.25rem] w-[46%] opacity-80 lg:block" style={DOTS} />

				{children}

				<div className="absolute right-[1.5rem] bottom-[1.25rem] z-10 hidden lg:block">
					<LanguageSwitcher variant="light" dropdownClass="dropdown-top dropdown-end" />
				</div>
			</section>
		</div>
	);
}

const STRIPES: CSSProperties = {
	backgroundImage:
		"linear-gradient(170deg,transparent 52%,#ed1c24 52.5%,#ed1c24 66%,transparent 66.5%),linear-gradient(170deg,transparent 70%,#ffcb05 70.5%)",
};

/** Red halftone, fading out from the top-right corner. */
const DOTS: CSSProperties = {
	backgroundImage: "radial-gradient(#ed1c24 0.09375rem, transparent 0.11875rem)",
	backgroundSize: "0.5625rem 0.5625rem",
	maskImage: "radial-gradient(ellipse 100% 100% at 100% 0%,#000 25%,rgba(0,0,0,.5) 55%,transparent 85%)",
	WebkitMaskImage: "radial-gradient(ellipse 100% 100% at 100% 0%,#000 25%,rgba(0,0,0,.5) 55%,transparent 85%)",
};

/**
 * The white card. Every step of every signed-out page renders inside one, its
 * children laid out on one 1rem grid like the prototype's `.lgn__card`.
 */
export function AuthCard({ children }: { children: ReactNode }) {
	return (
		<div className="relative z-[1] mx-auto grid w-full max-w-[27.5rem] gap-[1rem] rounded-[0.625rem] bg-white px-[1.25rem] py-[1.5rem] text-[0.90625rem] text-gms-navy-ink leading-[1.5] shadow-[0_24px_50px_-30px_rgba(9,10,40,0.7)] min-[45rem]:p-[2rem] lg:max-w-[28.75rem] lg:border lg:border-gms-line lg:p-[2.5rem] lg:shadow-[0_30px_60px_-40px_rgba(9,10,40,0.5)]">
			{children}
		</div>
	);
}
